import React, { createRef, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { Dimensions, Platform, StyleSheet, useWindowDimensions, View } from 'react-native'
import { useStore } from '../../../models'
import { Card } from '../Card'
import { ChipsLayout } from './ChipsLayout'
import { useTableTourPositioning } from '../../../hooks/usePositioning'
import { DS } from '../../../design-system'
import { numberWithCommas } from '../../../utils/numberWithCommas'
import { RoundWinner } from '../ModalContent/Content'
import { HandWinner } from '../HandWinner'
import MyFirstGameStepNames from '../../../models/game/first-game/MyFirstGameStepNames'
import { LinearGradient } from 'expo-linear-gradient'
import { useResponsive } from '@/screens/new-design/hooks'
import { Typography } from '@/design-system/Typography'
import { useLayoutShiftRegistry } from '@/hooks/useLayoutShiftRegistry'

const ThreeCardAmount = 1
const TexasHoldEmAmount = 5
const SINGLE_CARD_WIDTH = 57 + 4 //todo: get the correct width instead of this close approximation
const isWeb = Platform.OS === 'web'

export const TutorialBoard = observer(() => {
  const { register } = useLayoutShiftRegistry()
  const { width } = useWindowDimensions()
  const { verticalScale } = useResponsive({
    minVerticalScale: 0.7,
    maxVerticalScale: 1.2
  })
  const isSmallDevice = useMemo(() => width < 380, [width])
  const isSmallScreen = useMemo(() => width <= 550, [width])

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        container: {
          backgroundColor: '#892955',
          borderRadius: 100,
          flex: 1,
          justifyContent: 'space-around',
          alignItems: 'center',
          gap: 10,
          paddingHorizontal: DS.spacing[8],
          minWidth: 140
        },
        contentContainer: {
          justifyContent: 'center',
          gap: 30
        },
        cardsContainer: {
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap',
          maxWidth: '100%',
          flexBasis: '33%',
          gap: 4
        },
        chipsContainer: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 10
        },
        tooltipContainer: {
          position: 'absolute',
          zIndex: 99,
          left: -20,
          bottom: isSmallDevice ? 64 : 56,
          backgroundColor: 'red'
        }
      }),
    [isSmallDevice]
  )

  const webStyles = React.useMemo(
    () =>
      StyleSheet.create({
        // tooltipContainer: { position: 'absolute', left: 15, bottom: isSmallScreen ? 46 : -12 }
        tooltipContainer: { zIndex: 99 }
      }),
    [isSmallScreen]
  )

  const {
    game: {
      communityCards,
      mainPot,
      isThreeCard,
      isTableTour,
      isRaiseOrFold,
      isMyFirstGame,
      firstGameStage,
      stageInternalName,
      isGameOver
    }
  } = useStore()
  const { ref, onLayout } = useTableTourPositioning({ key: 'pot', shouldSave: () => true })
  const isBubbleCoveringBoard = [
    MyFirstGameStepNames.winnerNote,
    MyFirstGameStepNames.userTurnCall4,
    MyFirstGameStepNames.preFlopStrategy,
    MyFirstGameStepNames.estherPreCheck,
    MyFirstGameStepNames.estherTurnPreFold
  ].includes(stageInternalName)

  const amountOfCardsMissing = (isThreeCard ? ThreeCardAmount : TexasHoldEmAmount) - (communityCards?.length ?? 0)
  const boardCards = [...communityCards, ...Array.from({ length: amountOfCardsMissing }).map(() => undefined)]

  const getBoardPotText = () => {
    if (isTableTour || isRaiseOrFold || (isMyFirstGame && firstGameStage < 8)) return 'Pot'
    if (
      isMyFirstGame &&
      [MyFirstGameStepNames.beforeWinnerNote, MyFirstGameStepNames.winnerNote].includes(stageInternalName)
    ) {
      return 'You won!\n10,800'
    }
    return numberWithCommas(mainPot ?? 0)
  }

  const shouldShowCards = () => {
    if (
      isMyFirstGame &&
      [MyFirstGameStepNames.theFlop, MyFirstGameStepNames.theTurn, MyFirstGameStepNames.theRiver].includes(
        stageInternalName
      )
    ) {
      return false
    }

    return !(isRaiseOrFold || isGameOver)
  }

  const shouldApplyShadeToCard = (i: number) => {
    if (!isMyFirstGame) {
      return false
    }

    if (stageInternalName === MyFirstGameStepNames.roles) {
      return true
    }
    if ([MyFirstGameStepNames.beforeWinnerNote, MyFirstGameStepNames.winnerNote].includes(stageInternalName)) {
      return [0, 1, 2, 3].includes(i)
    }
  }

  return (
    <>
      {isMyFirstGame && stageInternalName === MyFirstGameStepNames.winnerNote && (
        <LinearGradient
          colors={['#FF3ED4', '#480D93', '#7B043D', '#FF7222', '#E9E238']}
          style={{ position: 'absolute', width: '104%', height: '104%', top: -3, borderRadius: 100 }}
          locations={[0.1, 0.3, 0.5, 0.7, 0.9]}
          start={{ x: 1, y: 1 }}
          end={{ x: 0, y: 0 }}
        />
      )}

      <View
        style={[
          styles.container,
          { opacity: isMyFirstGame && stageInternalName === MyFirstGameStepNames.roles ? 0.4 : 1 },
          isWeb ? { width: '100%', height: '100%' } : {}
        ]}
      >
        <View style={[styles.contentContainer, isBubbleCoveringBoard ? { paddingBottom: 120 } : {}]}>
          <View ref={ref} onLayout={onLayout} style={styles.chipsContainer} collapsable={false}>
            {isGameOver ? <HandWinner winnerCardSizes={16} /> : <RoundWinner />}
            {!isGameOver ? (
              <>
                <ChipsLayout />
                <DS.Text
                  variant="Heading 4/Regular"
                  color="Brand/Cream"
                  style={{
                    textAlign: 'center',
                    fontSize: verticalScale(Number(Typography['Heading 4/Regular'].fontSize)),
                    lineHeight: verticalScale(Number(Typography['Heading 4/Regular'].lineHeight))
                  }}
                >
                  {getBoardPotText()}
                </DS.Text>
              </>
            ) : null}
          </View>
          {shouldShowCards() ? (
            <>
              <View
                style={styles.cardsContainer}
                onLayout={(e) => register('myFirstGame.board.cards', e)}
                id="myFirstGame.board.cards"
              >
                {boardCards.map((card, i) => (
                  <View
                    key={`board-card-${card}-${i}`}
                    style={{
                      flexBasis: '30%'
                    }}
                  >
                    <Card
                      card={card}
                      disabled={shouldApplyShadeToCard(i)}
                      animate={false}
                      isOnTable={!(isMyFirstGame && stageInternalName === MyFirstGameStepNames.roles)}
                      fontSize={verticalScale(19)}
                    />
                  </View>
                ))}
              </View>
            </>
          ) : (
            <DS.Frame style={styles.cardsContainer}>
              <DS.Frame style={{ width: SINGLE_CARD_WIDTH * 3 }} />
            </DS.Frame>
          )}
        </View>
      </View>
    </>
  )
})
